import { EventEntityType } from '@kfolks/types'
import { z } from 'zod'

/**
 * Body validators for the generic `comment-service` HTTP API
 * (`POST /comments`, `PUT /comments/:id`).
 *
 * `entityType` is the `EventEntityType` enum (the unified entity-kind
 * enum used by both events and comments) — keeps the comments primitive
 * decoupled from any one consumer. `metadata.mentions` is an optional
 * UUID-array of resolved employee ids surfaced by the frontend's
 * `@employee` autocomplete; one `COMMENT_MENTIONED` event fans out per
 * newly-added entry on create / edit (Spec 4 notification routing keys
 * on this).
 *
 * Trim-and-min(1) on `content` so a whitespace-only comment is rejected
 * server-side — the UI's button-disable guard mirrors this rule.
 */

export const commentMetadataSchema = z.looseObject({
	mentions: z.array(z.uuid()).optional(),
})

export const commentCreateSchema = z.object({
	entityType: z.enum(EventEntityType),
	entityId: z.uuid(),
	content: z
		.string()
		.trim()
		.min(1, 'Comment content is required')
		.max(10_000, 'Comment is too long (max 10,000 chars)'),
	isInternal: z.boolean().optional(),
	metadata: commentMetadataSchema.optional(),
})

export type CommentCreateInput = z.infer<typeof commentCreateSchema>

export const commentUpdateSchema = commentCreateSchema
	.pick({ content: true, isInternal: true, metadata: true })
	.partial()

export type CommentUpdateInput = z.infer<typeof commentUpdateSchema>
